import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { formatDateOnly } from "../lib/datetime.js";
import { PLATFORM_TIME_ZONE, todayInZone } from "../lib/zone.js";

interface ClientRow {
  id: string;
  name: string;
}

interface LicenseRow {
  id: string;
  client_id: string;
  status: string;
  valid_from: string;
  valid_to: string | null;
  max_numbers: number | null;
  max_staff: number | null;
  notes: string | null;
}

const STATUSES = ["trial", "active", "suspended", "expired", "revoked"];

export function Licenses() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState("trial");
  const [validFrom, setValidFrom] = useState(todayInZone(PLATFORM_TIME_ZONE));
  const [validTo, setValidTo] = useState("");
  const [maxNumbers, setMaxNumbers] = useState("");
  const [maxStaff, setMaxStaff] = useState("");

  useEffect(() => {
    supabase
      .from("clients")
      .select("id, name")
      .order("name")
      .then(({ data }) => {
        const list = (data ?? []) as ClientRow[];
        setClients(list);
        if (list[0]) setClientId(list[0].id);
      });
  }, []);

  async function load() {
    if (!clientId) return;
    setLoading(true);
    const { data } = await supabase.from("licenses").select("*").eq("client_id", clientId).order("valid_from", { ascending: false });
    setRows((data ?? []) as LicenseRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!clientId) return;
    setError(null);
    const { error: err } = await supabase.from("licenses").insert({
      client_id: clientId,
      status,
      valid_from: validFrom,
      valid_to: validTo || null,
      max_numbers: maxNumbers ? Number(maxNumbers) : null,
      max_staff: maxStaff ? Number(maxStaff) : null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setValidTo("");
    setMaxNumbers("");
    setMaxStaff("");
    await load();
  }

  async function updateStatus(row: LicenseRow, newStatus: string) {
    await supabase.from("licenses").update({ status: newStatus }).eq("id", row.id);
    await load();
  }

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Licences</h1>

      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Client
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13 }}>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={fieldStyle}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>
          Valid from
          <input type="date" required value={validFrom} onChange={(e) => setValidFrom(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13 }}>
          Valid to (blank = open-ended)
          <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13 }}>
          Max numbers
          <input type="number" min={0} value={maxNumbers} onChange={(e) => setMaxNumbers(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          Max staff
          <input type="number" min={0} value={maxStaff} onChange={(e) => setMaxStaff(e.target.value)} style={{ ...fieldStyle, width: 90 }} />
        </label>
        <button type="submit" style={{ padding: "8px 16px" }}>
          Add
        </button>
      </form>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}

      {loading ? (
        <p className="rc-loading">Loading...</p>
      ) : (
        <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Status</th>
              <th style={{ padding: "10px 12px" }}>Valid from</th>
              <th style={{ padding: "10px 12px" }}>Valid to</th>
              <th style={{ padding: "10px 12px" }}>Max numbers</th>
              <th style={{ padding: "10px 12px" }}>Max staff</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>
                  <select value={r.status} onChange={(e) => void updateStatus(r, e.target.value)} style={{ padding: 4 }}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: "10px 12px" }}>{formatDateOnly(r.valid_from)}</td>
                <td style={{ padding: "10px 12px" }}>{r.valid_to ? formatDateOnly(r.valid_to) : "open-ended"}</td>
                <td style={{ padding: "10px 12px" }}>{r.max_numbers ?? "-"}</td>
                <td style={{ padding: "10px 12px" }}>{r.max_staff ?? "-"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No licences yet for this client.
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

const fieldStyle: React.CSSProperties = { display: "block", padding: 6, marginTop: 4 };
